/**
 * enhance_demo_controller.js — the public "try Chip AI" description demo.
 *
 * Drives templates/components/enhance_demo.html against the anonymous SSE
 * endpoint POST /chat/public/enhance. Rough notes in, a quote description
 * streamed out, then a registration CTA.
 *
 * The endpoint takes ONE field (notes). There is deliberately no instruction
 * box and no model picker on this surface, so there is nothing here to send
 * beyond what the visitor typed.
 *
 * Server frames: token {text} / done {full_text} / error {message, code?}.
 * Every link the error copy needs already lives in the template, so this
 * controller knows no routes but the endpoint it was handed.
 *
 * Result text is written with textContent, never innerHTML: it is model
 * output rendered on a public page.
 *
 * Analytics: PostHog, through _track(). The snippet is production-gated, so
 * window.posthog is undefined in local dev and every capture no-ops. The CTA
 * carries cta_source, the same placement vocabulary the demo and pricing CTAs
 * use, so this block can be told apart from the page's other signup paths.
 * Properties carry lengths only, NEVER the visitor's text.
 */

import { Controller } from "@hotwired/stimulus";

const CTA_SOURCE = "ai-tools-demo";
const EV_REWRITE_STARTED = "ai_demo_rewrite_started";
const EV_REWRITE_FINISHED = "ai_demo_rewrite_finished";
const EV_CTA_CLICKED = "ai_demo_cta_clicked";

const MSG_EMPTY = "Type a few notes about the job first.";
const MSG_BUSY = "Chip is copping a lot of requests right now. Give it a minute.";
const MSG_FAILED = "Chip could not finish that one. Give it another go.";
const MSG_NETWORK = "Network hiccup. Check your connection and give it another go.";

const COPIED_MS = 2000;

export default class extends Controller {
    static targets = ["input", "count", "go", "result", "copy", "cta",
                      "error", "errorMessage"];
    static values = { url: String };

    connect() {
        this._inFlight = false;
        this._copiedTimer = null;
        this._syncCount();
    }

    disconnect() {
        if (this._copiedTimer) clearTimeout(this._copiedTimer);
        if (this._abort) this._abort.abort();
    }

    // --- input ----------------------------------------------------------

    noteTyped() {
        this._syncCount();
    }

    useExample(event) {
        this.inputTarget.value = event.currentTarget.dataset.notes || "";
        this._syncCount();
        this.rewrite();
    }

    _syncCount() {
        if (this.hasCountTarget) {
            this.countTarget.textContent = String(this.inputTarget.value.length);
        }
    }

    // --- rewrite --------------------------------------------------------

    async rewrite() {
        if (this._inFlight) return;
        const notes = this.inputTarget.value.trim();
        if (!notes) {
            this._showError(MSG_EMPTY);
            this.inputTarget.focus();
            return;
        }
        this._begin(notes.length);
        try {
            const response = await this._post(notes);
            if (response) await this._consume(response);
        } catch (_err) {
            this._showError(MSG_NETWORK);
        } finally {
            this._end();
        }
    }

    // Returns the streaming response, or null once the failure is on screen.
    async _post(notes) {
        this._abort = new AbortController();
        const response = await fetch(this.urlValue, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept": "text/event-stream",
            },
            body: JSON.stringify({ notes: notes }),
            signal: this._abort.signal,
        });
        if (response.status === 429) {
            this._showError(MSG_BUSY);
            return null;
        }
        if (!response.ok || !response.body) {
            this._showError(MSG_FAILED);
            return null;
        }
        return response;
    }

    _begin(chars) {
        this._inFlight = true;
        this._text = "";
        this._hideError();
        this.goTarget.disabled = true;
        this.ctaTarget.hidden = true;
        this.copyTarget.hidden = true;
        this.resultTarget.textContent = "";
        this.element.classList.add("edemo--working");
        this._track(EV_REWRITE_STARTED, { input_chars: chars });
    }

    _end() {
        this._inFlight = false;
        this._abort = null;
        this.goTarget.disabled = false;
        this.element.classList.remove("edemo--working");
        if (!this._text) return;
        this.copyTarget.hidden = false;
        this.ctaTarget.hidden = false;
        this._track(EV_REWRITE_FINISHED, { output_chars: this._text.length });
    }

    // --- SSE ------------------------------------------------------------

    // Minimal SSE parser: frames split on blank lines, `event:` + `data:`
    // fields, JSON payloads. Same wire handling as public_chat_controller.js;
    // there is no shared reader module to import yet.
    async _consume(response) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = "";
        for (;;) {
            const chunk = await reader.read();
            if (chunk.done) break;
            buffer += decoder.decode(chunk.value, { stream: true });
            let sep;
            while ((sep = buffer.indexOf("\n\n")) !== -1) {
                this._handleFrame(buffer.slice(0, sep));
                buffer = buffer.slice(sep + 2);
            }
        }
    }

    _handleFrame(frame) {
        let event = "message";
        const dataLines = [];
        frame.split("\n").forEach(function (line) {
            if (line.indexOf("event:") === 0) event = line.slice(6).trim();
            else if (line.indexOf("data:") === 0) dataLines.push(line.slice(5).trim());
        });
        if (!dataLines.length) return;
        let data;
        try { data = JSON.parse(dataLines.join("\n")); } catch (_e) { return; }
        this._applyFrame(event, data);
    }

    _applyFrame(event, data) {
        if (event === "token") {
            this._text += data.text || "";
            this.resultTarget.textContent = this._text;
        } else if (event === "done") {
            this._text = data.full_text || this._text;
            this.resultTarget.textContent = this._text;
        } else if (event === "error") {
            this._text = "";
            this.resultTarget.textContent = "";
            this._showError(data.message || MSG_FAILED);
        }
    }

    // --- result actions --------------------------------------------------

    async copy() {
        if (!this._text) return;
        try {
            await navigator.clipboard.writeText(this._text);
        } catch (_err) {
            return;  // no clipboard permission: the text is on screen anyway
        }
        const button = this.copyTarget;
        const label = button.textContent;
        button.textContent = "Copied";
        if (this._copiedTimer) clearTimeout(this._copiedTimer);
        this._copiedTimer = setTimeout(function () {
            button.textContent = label;
        }, COPIED_MS);
    }

    ctaClicked() {
        this._track(EV_CTA_CLICKED, { cta_source: CTA_SOURCE });
    }

    // --- errors ----------------------------------------------------------

    _showError(message) {
        this.errorMessageTarget.textContent = message;
        this.errorTarget.hidden = false;
    }

    _hideError() {
        this.errorTarget.hidden = true;
    }

    // --- analytics --------------------------------------------------------

    _track(name, properties) {
        if (typeof window.posthog !== "undefined") {
            window.posthog.capture(name, Object.assign(
                { cta_source: CTA_SOURCE }, properties || {}));
        }
    }
}
